import React, { useEffect, useState } from "react";
import { EyeOff, Eye, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getAnonMode, enterAnonMode, exitAnonMode } from "../../lib/anonMode";

/**
 * Anon Mode toggle panel — lives on /profile.
 *
 * Behaviour
 * =========
 * - When OFF: shows a single, ominous "ENTER ANON MODE" CTA.
 * - When ON:  shows the active alias (uppercase mono) + "EXIT ANON MODE"
 *             button. While active, every thread / comment the user posts
 *             gets stamped with that alias + `is_anon: true` server-side.
 * - Previous posts are NEVER rewritten — the toggle only affects future
 *   activity, and posts authored in anon mode keep their alias forever
 *   (no retroactive reveal even after toggling off).
 */
export default function AnonModePanel() {
  const [state, setState] = useState({ active: false, alias: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await getAnonMode();
        if (alive) setState(s);
      } catch (e) {
        if (alive) setState({ active: false, alias: null });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const onToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = state.active ? await exitAnonMode() : await enterAnonMode();
      setState(next);
      if (next.active) {
        toast.success(`Anon Mode active — you are ${next.alias}`);
      } else {
        toast.success("Anon Mode off — back on the record");
      }
    } catch (e) {
      toast.error("Couldn't flip the switch");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[#ef4444]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#ef4444]/[0.08] to-[#ef4444]/[0.02] p-5 font-mono text-[10px] tracking-[0.25em] text-[#ef4444]/70 uppercase">
        Loading disguise…
      </div>
    );
  }

  return (
    <div
      data-testid="anon-mode-panel"
      className="relative border-2 overflow-hidden p-5 sm:p-6"
      style={{
        borderColor: state.active ? "#ef4444" : "rgba(239,68,68,0.4)",
        background: state.active
          ? "radial-gradient(circle at 30% 30%, rgba(239,68,68,0.10), transparent 65%), #050505"
          : "linear-gradient(135deg, rgba(239,68,68,0.10), rgba(239,68,68,0.02))",
        boxShadow: state.active ? "0 0 0 1px #ef444433, 0 20px 60px -25px #ef444466" : undefined,
      }}
    >
      {/* Top stripe */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ backgroundColor: state.active ? "#ef4444" : "rgba(255,255,255,0.10)" }}
      />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div
            className="font-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-1.5 mb-2"
            style={{ color: state.active ? "#ef4444" : "#71717a" }}
          >
            {state.active ? <Zap size={11} /> : <EyeOff size={11} />}
            // {state.active ? "ANON MODE · ENGAGED" : "ANON MODE · OFFLINE"}
          </div>
          {state.active ? (
            <>
              <div className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight">
                {state.alias || "REDACTED"}
              </div>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed max-w-xl">
                Posts &amp; comments you make right now are stamped with this alias
                and disconnected from your real handle. Your coins, cards, sigils,
                bookmarks &amp; follows still belong to you — only the public
                surface is masked.
              </p>
              <div className="mt-3 flex items-start gap-2 text-amber-300/80 text-[11px] font-mono tracking-[0.15em] uppercase">
                <AlertTriangle size={11} className="mt-[2px] shrink-0" />
                <span>Exiting will not un-disguise past anon posts.</span>
              </div>
            </>
          ) : (
            <>
              <div className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight">
                Go Off The Record.
              </div>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed max-w-xl">
                Flip into a single-session disguise — the vault gives you a
                throwaway codename (e.g. <span className="font-mono text-zinc-300">GHOST_PROTOCOL_47</span>).
                Every thread / comment you post stays untraceable to your handle.
                Your inventory, follows &amp; DMs keep working as you.
              </p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          data-testid={state.active ? "anon-mode-exit-btn" : "anon-mode-enter-btn"}
          className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.25em] uppercase transition-colors shrink-0 disabled:opacity-50 ${
            state.active
              ? "border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-[#050505]"
              : "bg-white text-[#050505] hover:bg-zinc-200"
          }`}
        >
          {state.active ? <Eye size={11} /> : <EyeOff size={11} />}
          {busy
            ? "Working…"
            : state.active
            ? "Exit Anon Mode"
            : "Enter Anon Mode"}
        </button>
      </div>
    </div>
  );
}
